import './ShareHintDetail.scss';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import {
    SharingLinkKind,
    ISharingLinkSettings,
    SharingAudience,
    ISharingInformation,
    SharingRole,
    ICustomLabeledAudienceConfig,
    ClientId
}
    from '../../../interfaces/SharingInterfaces';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import * as React from 'react';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import stringsResx from '../../Share.resx';
import { getShortDisplayDate } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import { getCustomLinkLabel } from '../../../utilities/ShareHelper';

const EnableLinkSettingsEditPanel = { ODB: 778 };
export interface IShareHintDetailProps {
    clientId: ClientId;
    companyName: string;
    currentSettings: ISharingLinkSettings;
    sharingInformation: ISharingInformation;
    linkRecipients: Array<IPerson>;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
}

export class ShareHintDetail extends React.Component<IShareHintDetailProps, {}> {
    constructor(props: IShareHintDetailProps, context: any) {
        super(props);

        this._renderStatusIcons = this._renderStatusIcons.bind(this);
    }

    public render(): React.ReactElement<{}> {
        const label: string = this._getLabel(this.props.currentSettings);

        return (
            <div className='od-ShareHintDetail'>
                <div className='od-ShareHintDetail-description'>
                    <div className='od-ShareHintDetail-status'>
                        <span className='od-ShareHintDetail-statusText'>{ label }</span>
                        { this._renderStatusIcons() }
                    </div>
                </div>
            </div>
        );
    }

    private _renderStatusIcons(): JSX.Element {
        const expiration = this.props.currentSettings.expiration;
        const password = this.props.currentSettings.password;
        const preventDownloadLink = this.props.currentSettings.preventDownload;
        const shareId = this.props.currentSettings.shareId;
        let iconData: Array<any> = [];

        if (expiration && (expiration.getTime() !== (new Date(0)).getTime())) {
            // TODO: remove when new string is localized.
            if (FeatureOverrides.isFeatureEnabled(EnableLinkSettingsEditPanel)) {
                iconData.push({
                    icon: 'Calendar',
                    tooltip: `${StringHelper.format(stringsResx.expiresIn2, getShortDisplayDate(expiration))}`
                });
            } else {
                iconData.push({
                    icon: 'Calendar'
                });
            }
        }
        // ShareId is only set when we're currently on a passworded template
        if ((shareId && shareId.length > 0) || password.length > 0) {
            iconData.push({
                icon: 'Lock',
                tooltip: stringsResx.sharePasswordReminderText
            });
        }

        if (preventDownloadLink) {
            iconData.push({
                icon: 'Blocked2',
                tooltip: stringsResx.preventDownloadHint
            });
        }
        const iconElements: JSX.Element[] = iconData.map((data: any, index: number) => {
            return (
                <i className='od-ShareHintDetail-item'>
                    <TooltipHost content={ data.tooltip } id={ data.icon }>
                        <Icon
                            iconName={ data.icon }
                            aria-describedby={ data.icon }
                        />
                    </TooltipHost>
                </i>
            );
        });

        return (
            <div className='od-ShareHintDetail-iconsList'>
                { iconElements }
            </div>
        );
    }

    private _getLabel(currentSettings: ISharingLinkSettings): string {
        // If currentSettings hasn't been initialized, just don't
        // show a hint yet.
        if (!currentSettings) {
            return '';
        }

        const strings = stringsResx;
        const companyName = this.props.companyName;

        // Prevent Download sharing hint detail.
        if (currentSettings.preventDownload) {
            if (currentSettings.audience === SharingAudience.anyone) {
                return strings.anonymousViewOnlyHint;
            } else if (currentSettings.audience === SharingAudience.organization) {
                return StringHelper.format(strings.cslViewOnlyHint, companyName);
            } else if (currentSettings.audience === SharingAudience.specificPeopleFlex) {
                return strings.specificPeopleViewOnlyHint;
            }
        }

        switch (currentSettings.sharingLinkKind) {
            case SharingLinkKind.direct:
                if (currentSettings.audience === SharingAudience.specificPeople) {
                    if (!currentSettings.sharingRole) {
                        return currentSettings.isEdit ? strings.specificPeopleEditHint : strings.specificPeopleViewHint;
                    } else {
                        if (currentSettings.sharingRole === SharingRole.edit) {
                            return strings.specificPeopleEditHint;
                        } else if (currentSettings.sharingRole === SharingRole.review) {
                            return stringsResx.specificPeopleReviewHint;
                        } else {
                            return strings.specificPeopleViewHint;
                        }
                    }
                } else {
                    return strings.existingPeopleDescription;
                }
            case SharingLinkKind.organizationView:
                return StringHelper.format(strings.cslViewDescription, companyName);
            case SharingLinkKind.organizationEdit:
                return StringHelper.format(strings.cslEditDescription, companyName);
            case SharingLinkKind.anonymousView:
                return strings.anonViewDescription;
            case SharingLinkKind.anonymousEdit:
                return strings.anonEditDescription;
            case SharingLinkKind.flexible:
                if (currentSettings.audience === SharingAudience.specificPeopleFlex) {
                    if (!currentSettings.sharingRole) {
                        return currentSettings.isEdit ? strings.specificPeopleEditHint : strings.specificPeopleViewHint;
                    } else {
                        if (currentSettings.sharingRole === SharingRole.edit) {
                            return strings.specificPeopleEditHint;
                        } else if (currentSettings.sharingRole === SharingRole.review) {
                            return stringsResx.specificPeopleReviewHint;
                        } else {
                            return strings.specificPeopleViewHint;
                        }
                    }
                } else if (currentSettings.audience === SharingAudience.customLabeledSpecificPeople) {
                    if (currentSettings.isEdit) {
                        return this.props.customLabeledAudienceConfig && !!this.props.customLabeledAudienceConfig.entityName ?
                            StringHelper.format(stringsResx.customLabeledEditLabel, this.props.customLabeledAudienceConfig.entityName) :
                            getCustomLinkLabel(this.props.clientId, 'edit', this.props.customLabeledAudienceConfig);
                    } else if (currentSettings.sharingRole === SharingRole.review) {
                        return this.props.customLabeledAudienceConfig && !!this.props.customLabeledAudienceConfig.entityName ?
                            StringHelper.format(stringsResx.customLabeledReviewLabel, this.props.customLabeledAudienceConfig.entityName) :
                            getCustomLinkLabel(this.props.clientId, 'review', this.props.customLabeledAudienceConfig);
                    } else {
                        return this.props.customLabeledAudienceConfig && !!this.props.customLabeledAudienceConfig.entityName ?
                            StringHelper.format(stringsResx.customLabeledViewLabel, this.props.customLabeledAudienceConfig.entityName) :
                            getCustomLinkLabel(this.props.clientId, 'view', this.props.customLabeledAudienceConfig);
                    }
                }
                return '';
            default:
                return '';
        }
    }
}